import { ProgramEngagement, ProgramEngagementModel, ProgramEngagementStatus } from '@cibo/core'
import { Swatch, intuitiveFromNow } from '@cibo/ui'
import { Stack, Typography, useTheme } from '@mui/material'
import {
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'

const SwatchCell = ({ value }: GridRenderCellParams<ProgramEngagementStatus>) => {
  const { palette } = useTheme()

  if (value?.available) {
    if (value.status.progress?.completed) {
      return <Swatch color={palette['success'].main} />
    } else {
      return <Swatch color={palette['warning'].main} />
    }
  } else {
    return <Swatch color={palette['error'].main} />
  }
}

export const useEngagementsColumns = () => {
  const { t } = useTranslation('@cibo/programs/PaginatedEngagementsTable')

  return {
    applicationStatus: {
      field: 'applicationStatus',
      minWidth: 32,
      maxWidth: 32,
      valueGetter: ({ row }: GridValueGetterParams<any, ProgramEngagement>) =>
        new ProgramEngagementModel(row).getStageStatus('apply'),
      renderCell: SwatchCell,
    },
    owner: {
      field: 'owner',
      headerName: t('owner'),
      flex: 2,
      valueGetter: ({
        row: {
          ownedBy: { familyName, givenName, userPrimaryEmail, userId },
        },
      }: GridValueGetterParams<any, ProgramEngagementModel>) => userPrimaryEmail,
      renderCell: ({
        row: {
          ownedBy: { familyName, givenName, userPrimaryEmail, userId },
        },
      }: GridRenderCellParams<ProgramEngagementModel>) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {!!givenName || !!familyName ? `${givenName} ${familyName}` : userPrimaryEmail}
        </Typography>
      ),
    },
    metadata: {
      field: 'metadata',
      headerName: t('metadata'),
      flex: 1,
      align: 'right',
      renderCell: ({
        row: { fieldCounts, updatedAt },
      }: GridRenderCellParams<ProgramEngagementModel>) => (
        <Stack justifyContent="center" alignItems="flex-end">
          <Typography variant="body2">{t('fields', { count: fieldCounts.total })}</Typography>
          <Typography variant="caption">{intuitiveFromNow(updatedAt)}</Typography>
        </Stack>
      ),
    },
    currentStepType: {
      field: 'currentStep',
      headerName: t('stepType'),
      minWidth: 100,
      valueFormatter: ({ value }: GridValueFormatterParams<string>) =>
        t(`stepType_${value && value.replace(':', '-')}`, {
          ns: '@cibo/programs/PaginatedEngagementsTable',
        }),
    },
  }
}
